import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLinkIcon } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { ActionsCreateButton } from './Actions.createButton';
import { QueryKey } from '@/Config/api/queryKey';
import { ActionDto, AIActionUsageStatsDto } from '@/Domain/api/Action/dto/Action.dto';
import { CampaignDto } from '@/Domain/api/Action/dto/Campaign.dto';
import { ShopifyMcpDto } from '@/Domain/api/Action/dto/ShopifyMcp.dto';
import { AiActionList } from '@/Domain/components/Action/AiActionList/AiActionList';
import { AppActionsList } from '@/Domain/components/Action/AppActionList/AppActionList';
import { CreateHttpRequestActionDialog } from '@/Domain/components/Action/HttpRequestActionFromText/CreateHttpRequestAction.modal';
import { HttpRequestActionSheet } from '@/Domain/components/Action/HttpRequestActionSheet/HttpRequestAction.sheet';
import { HubSpotTemplateGalleryDialog } from '@/Domain/components/Action/HubSpotTemplates/HubSpotTemplateGallery.dialog';
import { KnowledgeBaseActionSheet } from '@/Domain/components/Action/KnowledgeBaseAction/KnowledgeBaseAction.sheet';
import { RemoteMcpActionSheet } from '@/Domain/components/Action/RemoteMCPAction/RemoteMcpAction.sheet';
import { ShopifyMcpActionDialog } from '@/Domain/components/Action/ShopifyMcpAction/ShopifyMcpAction.dialog';
import { useTrackPageVisit } from '@/Domain/components/OnboardingProgress';
import { Field, FieldDescription, FieldLabel } from '@/Domain/components/Shared/Field/Field';
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemMedia,
  ItemTitle,
} from '@/Domain/components/Shared/Item/Item';
import { Separator } from '@/Domain/components/Shared/Separator/Separator';
import { ActionTypeEnum } from '@/Domain/enum/ActionType.enum';
import { useActionLimit } from '@/Domain/hooks/Action/useActionLimit.hook';
import { MODAL_NAMES, useModalStore } from '@/Domain/store/modal.store';
import {
  APP_CALDOTCOM,
  APP_CALENDLY,
  APP_HUBSPOT,
  APP_OTHER_API_ID,
  APP_OTHER_MCP,
  APP_SHOPIFY,
  APP_STRIPE,
  CHANNEL_API,
  OPEN_MODAL_QUERY_PARAM,
} from '@/UI/GettingStarted/components/onboardingOptions';

type Props = {
  actions: ActionDto[];
  stats?: Record<string, AIActionUsageStatsDto>;
};

export const ActionsPage = ({ actions, stats }: Props) => {
  const queryClient = useQueryClient();
  useTrackPageVisit('actions');

  const { t } = useTranslation('actions_page');
  const campaigns = queryClient.getQueryData<CampaignDto[]>([QueryKey.campaign.list]);
  const { tierLimit, isFetched: isSubscriptionFetched } = useActionLimit();

  const { data: knowledgeBaseTags, isLoading: isKnowledgeBaseTagsLoading } = useQuery<string[]>({
    queryKey: [QueryKey.knowledgeBase.tags],
  });

  const { toggleModal } = useModalStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const openParam = searchParams.get(OPEN_MODAL_QUERY_PARAM);
  const handledOpenRef = useRef<string | null>(null);

  const createShopifyMcpForDeepLinkMutation = useMutation<ShopifyMcpDto, unknown, void>({
    mutationKey: [QueryKey.actions.shopifyMcp.create],
  });

  useEffect(() => {
    if (!openParam || handledOpenRef.current === openParam) return;
    handledOpenRef.current = openParam;

    const clearOpenParam = () =>
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete(OPEN_MODAL_QUERY_PARAM);
          return next;
        },
        { replace: true },
      );

    switch (openParam) {
      case APP_HUBSPOT:
        toggleModal(MODAL_NAMES.HUBSPOT_TEMPLATE_GALLERY_DIALOG);
        clearOpenParam();
        break;
      case APP_SHOPIFY: {
        const existingShopifyAction = (actions || []).find(
          (action) => action.type === ActionTypeEnum.ShopifyRemoteMcp,
        );
        if (existingShopifyAction) {
          clearOpenParam();
          break;
        }
        createShopifyMcpForDeepLinkMutation
          .mutateAsync()
          .then((shopifyMcpAction) => {
            toggleModal(MODAL_NAMES.SHOPIFY_MCP_DIALOG, { shopifyMcpAction });
          })
          .finally(clearOpenParam);
        break;
      }
      case APP_STRIPE:
      case APP_CALDOTCOM:
      case APP_CALENDLY:
      case CHANNEL_API:
      case APP_OTHER_API_ID:
        toggleModal(MODAL_NAMES.CREATE_HTTP_REQUEST_ACTION_DIALOG);
        clearOpenParam();
        break;
      case 'remote_mcp':
      case APP_OTHER_MCP:
        toggleModal(MODAL_NAMES.REMOTE_MCP_DIALOG);
        clearOpenParam();
        break;
      default:
        clearOpenParam();
        break;
    }
  }, [openParam, actions, toggleModal, setSearchParams, createShopifyMcpForDeepLinkMutation]);

  return (
    <div className="max-w-7xl mx-auto flex flex-col h-full">
      <h1 className="text-lg font-semibold text-foreground">{t('title')}</h1>
      <Item
        variant="muted"
        className="mt-2 w-fit hover:bg-accent"
        size="sm"
        render={
          <a
            href="https://docs.quickchat.ai/ai-agent/actions"
            target="_blank"
            rel="noopener noreferrer"
          >
            <ItemMedia variant="image">
              <img
                src="/static/assets/action_banner.png"
                alt={t('banner.title')}
                width={24}
                height={24}
                className="object-cover"
              />
            </ItemMedia>
            <ItemContent>
              <ItemTitle className="line-clamp-1">{t('banner.title')}</ItemTitle>
              <ItemDescription>{t('banner.description')}</ItemDescription>
            </ItemContent>
            <ItemActions>
              <ExternalLinkIcon className="ml-5 size-4 text-foreground-subtle" />
            </ItemActions>
          </a>
        }
      />

      <div className="space-y-8 mt-8 flex flex-col flex-1">
        {campaigns?.length && (
          <section>
            <Field>
              <FieldLabel>{t('quickchat_actions_title')}</FieldLabel>
              <FieldDescription>{t('quickchat_actions_subtitle')}</FieldDescription>
              <AppActionsList campaigns={campaigns || []} />
            </Field>
          </section>
        )}

        <Separator className="mt-7" />
        <section className="flex flex-col flex-1">
          <Field>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-col gap-1.5">
                <FieldLabel>{t('api_actions_title')}</FieldLabel>
                <FieldDescription>{t('api_actions_description')}</FieldDescription>
              </div>
              <div className="flex items-center justify-between gap-3 sm:justify-end sm:gap-6">
                {isSubscriptionFetched && (
                  <p className="text-sm font-medium text-muted-foreground">
                    {t('list.remaining', { count: tierLimit })}
                  </p>
                )}
                <ActionsCreateButton actions={actions || []} />
              </div>
            </div>
            <AiActionList actions={actions || []} stats={stats} />
          </Field>
        </section>
      </div>

      <HttpRequestActionSheet />
      {!isKnowledgeBaseTagsLoading ? (
        <KnowledgeBaseActionSheet knowledgeBaseTags={knowledgeBaseTags!} />
      ) : null}
      <ShopifyMcpActionDialog />
      <RemoteMcpActionSheet />
      <CreateHttpRequestActionDialog />
      <HubSpotTemplateGalleryDialog />
    </div>
  );
};
